import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { Separator } from '@buildoutinc/blueprint-react/ui/Separator';
import { pluralize } from 'helpers/formatters/string_formats';

import type { AssistantQuestion } from './types';

interface Props {
  answers: (string | null)[] | null;
  note: string | null;
  questions: AssistantQuestion[];
}

// Stands in for the card once it can no longer be answered, so the transcript still shows what was
// asked instead of a gap where a question used to be.
export function QuestionSummary({ answers, note, questions }: Props) {
  const headline = getHeadline(answers, note, questions.length);

  return (
    <Card>
      <Card.Body className="d-flex flex-column gap-2">
        {headline && (
          <div className="d-flex align-items-center gap-2">
            {answers?.some(Boolean) && <FontAwesomeIcon className="text-success" icon={faCircleCheck} />}
            <span className="text-muted">{headline}</span>
          </div>
        )}
        {questions.map((question, index) => (
          <React.Fragment key={question.question}>
            {index > 0 && <Separator />}
            <div>
              <div className="fs-small text-muted">{question.question}</div>
              {answers && (
                <div className={answers[index] ? 'fw-semibold' : 'text-muted fst-italic'}>
                  {answers[index] || 'Skipped'}
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </Card.Body>
    </Card>
  );
}

function getHeadline(answers: (string | null)[] | null, note: string | null, asked: number) {
  if (!answers) {
    return note;
  }

  const answered = answers.filter(Boolean).length;

  return answered === asked
    ? `${pluralize(answered, 'question')} answered`
    : `${answered} of ${pluralize(asked, 'question')} answered`;
}
